// ---------------------------------------------------------------------------
// Canonical expression-prefix vocabulary for the flow editor (F43/F38/F44).
//
// Source-of-truth: `@cogo/validator` `CANONICAL_EXPR_PREFIXES` /
// `LEGACY_EXPR_PREFIXES` (src/stages/stage-actionflow.ts). The validator
// package's root entry is not browser-safe (node:fs loaders), so the studio
// carries this mirror; `__tests__/expression-prefixes.test.ts` locks parity
// against the validator export so the two can never drift.
// ---------------------------------------------------------------------------

/** The 9 canonical prefixes — the ONLY vocabulary the editor teaches. */
export const CANONICAL_PREFIXES = [
  'local',
  'event',
  'props',
  'item',
  '_results',
  'computed',
  'appData',
  'env',
  'route',
] as const

/** Validator-rejected legacy prefixes → creator-facing migration hint. */
export const LEGACY_PREFIX_HINTS: Record<string, string> = {
  state: 'local.',
  localState: 'local.',
  response: '_results.',
  ui: 'appData.',
  params: 'route.params or props.',
  page: 'appData.',
}

/** `route.` has a fixed read-only sub-structure — suggest the real paths. */
export const ROUTE_PATHS = ['route.params', 'route.path', 'route.query', 'route.hash'] as const

// Matches `#{prefix.` even while the expression is still being typed (no
// closing brace required) — inline education must fire mid-keystroke — and
// the closed dot-less form `#{prefix}`, which the validator equally rejects
// (its scanExpressionPrefixes makes the `.path` tail optional). A bare open
// `#{prefix` without dot or brace stays unmatched: the user may still be
// typing a longer identifier.
const PREFIX_IN_EXPRESSION_RE = /#\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:\.|\})/g

/**
 * Unique legacy prefixes referenced inside `#{...}` expressions in `text`.
 * Returns [] for canonical/unknown prefixes — the warning is legacy-specific.
 */
export function findLegacyExpressionPrefixes(text: string): string[] {
  if (typeof text !== 'string' || !text.includes('#{')) return []
  const found = new Set<string>()
  let m: RegExpExecArray | null
  PREFIX_IN_EXPRESSION_RE.lastIndex = 0
  while ((m = PREFIX_IN_EXPRESSION_RE.exec(text))) {
    if (m[1] in LEGACY_PREFIX_HINTS) found.add(m[1])
  }
  return [...found]
}

/** setState.params.target contract: BARE dot-path, no `#{}` wrapper. */
export const BARE_TARGET_RE = /^[A-Za-z_][\w.]*$/

/**
 * Validates a setState target as a bare variable name. Returns a
 * creator-facing error string, or null when the input is acceptable.
 * Empty input returns null — required-ness is the field's concern.
 */
export function validateBareTargetName(input: string): string | null {
  if (input === '') return null
  if (/[#{}]/.test(input)) {
    return 'Use the bare variable name — no #{…} wrapper (e.g. "email")'
  }
  if (!BARE_TARGET_RE.test(input)) {
    return 'Variable names start with a letter or _ and use only letters, digits, _ and dots'
  }
  const firstSegment = input.split('.')[0]
  if (firstSegment in LEGACY_PREFIX_HINTS) {
    return `"${firstSegment}." is a legacy prefix — use the bare variable name (e.g. "email")`
  }
  // Validator parity (actionflow-setstate-local-prefix, v5.8): a "local."
  // prefixed target writes to a DIFFERENT namespace than the bare key the
  // UI bindings read — hard error, mirror it inline.
  if (firstSegment === 'local' && input.includes('.')) {
    return `"local." prefix is not allowed here — use "${input.slice('local.'.length)}" (bare name)`
  }
  return null
}
